import { Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Clock,
  Download,
  ExternalLink,
  Eye,
  MoreHorizontal,
  Pencil,
  Play,
  Trash2,
} from 'lucide-react';
import { Menu, type MenuItem } from '@/components/ui/Menu';
import type { RecordedClass } from '@/types/recording';
import { SubjectThumbnailCard, getSubjectCategory } from './SubjectThumbnailCard';
import { formatVideoLength } from '@/lib/format';
import { formatInstituteDateTime } from '../../utils';
import { lessonLinkState, lessonPath } from './lessonLink';

interface RecordedClassesTableProps {
  recordings: RecordedClass[];
  onEdit: (recording: RecordedClass) => void;
  onRemove: (recording: RecordedClass) => void;
  /**
   * Puts a heading above each run of lessons sharing a subject or a posted day.
   * The rows must already be sorted that way — this only marks where a run starts.
   */
  groupBy?: 'subject' | 'date';
}

/**
 * The day a lesson was posted, as the institute reads it — in IST, not the
 * device's zone, so a lesson posted just after midnight sits under the right
 * day's heading wherever the teacher happens to be.
 */
function formatPostedDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function RecordedClassesTable({
  recordings,
  onEdit,
  onRemove,
  groupBy,
}: RecordedClassesTableProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Carries this library view along, so the lesson's Back returns to the same
  // class, filters and page rather than to a fresh library.
  const openLesson = (uuid: string) =>
    navigate(lessonPath(uuid), { state: lessonLinkState(location.pathname + location.search) });

  const groupLabelOf = (recording: RecordedClass) =>
    groupBy === 'subject'
      ? recording.subject.name
      : formatPostedDay(recording.postedAt);

  /** The heading for this row, when it is the first of its group; otherwise null. */
  const groupHeadingAt = (index: number): string | null => {
    if (!groupBy) return null;
    const label = groupLabelOf(recordings[index]);
    return index === 0 || groupLabelOf(recordings[index - 1]) !== label ? label : null;
  };

  // One list for both layouts' menus, so the phone and desktop rows can never
  // offer different actions.
  const actionsFor = (recording: RecordedClass): MenuItem[] => [
    {
      key: 'view-details',
      label: 'View Details',
      icon: <ExternalLink className="h-4 w-4 text-ink-3" />,
      onSelect: () => openLesson(recording.uuid),
    },
    {
      key: 'edit-lesson',
      label: 'Edit Lesson',
      icon: <Pencil className="h-4 w-4 text-ink-3" />,
      onSelect: () => onEdit(recording),
    },
    {
      key: 'suspend-lesson',
      label: 'Suspend Lesson',
      icon: <Trash2 className="h-4 w-4" />,
      tone: 'danger',
      onSelect: () => onRemove(recording),
    },
  ];

  return (
    <div className="card bg-surface border border-line rounded-[16px] overflow-hidden shadow-2xs">
      {/* Mobile Card View (shown on screens < 768px) */}
      <div className="block md:hidden divide-y divide-line">
        {recordings.map((recording, index) => {
          const heading = groupHeadingAt(index);
          const postedDay = formatPostedDay(recording.postedAt);
          const durationStr = formatVideoLength(recording.durationSeconds) ?? '—';
          const category = getSubjectCategory(recording.subject.name);

          return (
            <Fragment key={recording.uuid}>
              {heading && <h3 className="lbl bg-surface-2 px-3.5 py-2">{heading}</h3>}
              <div className="p-3.5 flex flex-col gap-3">
                {/* Thumbnail & Info */}
                <div className="flex items-center gap-3">
                  <div className="shrink-0">
                    <SubjectThumbnailCard
                      recording={recording}
                      onClick={() => openLesson(recording.uuid)}
                    />
                  </div>
                  <div className="min-w-0 flex-1 flex flex-col gap-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-[6px] text-2xs font-extrabold uppercase tracking-wider ${category.badge}`}
                      >
                        {recording.subject.name}
                      </span>
                      <span className="inline-flex items-center gap-1 text-2xs font-bold text-ink-2 bg-surface-2 px-2 py-0.5 rounded-[6px] border border-line">
                        <Clock className="h-3 w-3 text-ink-3" />
                        {durationStr}
                      </span>
                    </div>
                    <h3
                      onClick={() => openLesson(recording.uuid)}
                      className="text-sm font-bold text-ink hover:text-indigo-400 cursor-pointer line-clamp-1"
                    >
                      {recording.title}
                    </h3>
                    {recording.topic && (
                      <p className="text-xs text-ink-3 line-clamp-1">{recording.topic}</p>
                    )}
                  </div>
                </div>

                {/* Bottom Metadata & Actions */}
                <div className="flex items-center justify-between gap-2 pt-1 border-t border-line/60">
                  <div className="flex items-center gap-2 text-2xs font-medium text-ink-3">
                    <span>{postedDay}</span>
                    <span>•</span>
                    <span className="inline-flex items-center gap-1 font-bold text-ink-2">
                      <Eye className="h-3.5 w-3.5 text-ink-3" />
                      {recording.viewCount ?? 0}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      title="Watch lesson"
                      onClick={() => openLesson(recording.uuid)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500/15 text-indigo-500 border border-indigo-500/30 hover:bg-indigo-600 hover:text-white transition-all cursor-pointer shadow-2xs"
                    >
                      <Play className="h-3.5 w-3.5 fill-current" />
                    </button>

                    <button
                      type="button"
                      title="Download or open link"
                      onClick={() => {
                        const url =
                          recording.downloadUrl || recording.playbackUrl || recording.videoUrl;
                        if (url) {
                          window.open(url, '_blank', 'noopener,noreferrer');
                        } else {
                          openLesson(recording.uuid);
                        }
                      }}
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/15 text-blue-500 border border-blue-500/30 hover:bg-blue-600 hover:text-white transition-all cursor-pointer shadow-2xs"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>

                    <Menu
                      label={`Actions for ${recording.title || 'lesson'}`}
                      align="end"
                      trigger={<MoreHorizontal className="h-4 w-4" />}
                      triggerClassName="flex h-8 w-8 items-center justify-center rounded-lg bg-surface-2 text-ink-3 border border-line hover:text-ink transition-all cursor-pointer shadow-2xs"
                      items={actionsFor(recording)}
                    />
                  </div>
                </div>
              </div>
            </Fragment>
          );
        })}
      </div>

      {/* Desktop / Tablet Table View (shown on screens >= 768px) */}
      <div className="hidden md:block overflow-x-auto table-scrollbar">
        <table className="w-full text-left border-collapse min-w-[800px]">
          <thead>
            <tr className="bg-surface text-[11px] font-bold text-ink-3 uppercase tracking-wider border-b border-line">
              <th className="py-3.5 px-4 w-48">Class / Subject</th>
              <th className="py-3.5 px-4">Topic</th>
              <th className="py-3.5 px-4 w-28 text-center">Duration</th>
              <th className="py-3.5 px-4 w-36">Posted</th>
              <th className="py-3.5 px-4 w-24 text-center">Views</th>
              <th className="py-3.5 px-4 w-32 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {recordings.map((recording, index) => {
              const heading = groupHeadingAt(index);
              const durationStr = formatVideoLength(recording.durationSeconds) ?? '—';
              const category = getSubjectCategory(recording.subject.name);

              return (
                <Fragment key={recording.uuid}>
                  {heading && (
                    <tr className="bg-surface-2">
                      <th colSpan={6} scope="colgroup" className="lbl px-4 py-2 text-left">
                        {heading}
                      </th>
                    </tr>
                  )}
                  <tr className="group hover:bg-slate-50/80 dark:hover:bg-surface-2 transition-colors">
                    {/* 1. Class / Subject Thumbnail */}
                    <td className="py-3.5 px-4 align-middle">
                      <SubjectThumbnailCard
                        recording={recording}
                        onClick={() => openLesson(recording.uuid)}
                      />
                    </td>

                    {/* 2. Topic & Details */}
                    <td className="py-3.5 px-4 align-middle">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-[6px] text-2xs font-extrabold uppercase tracking-wider ${category.badge}`}
                          >
                            {recording.subject.name}
                          </span>
                        </div>
                        <p
                          onClick={() => openLesson(recording.uuid)}
                          className="text-sm font-semibold text-ink-2 hover:text-indigo-400 cursor-pointer truncate"
                          title={recording.title}
                        >
                          {recording.title}
                        </p>
                        {recording.topic && (
                          <p className="text-xs text-ink-3 truncate" title={recording.topic}>
                            {recording.topic}
                          </p>
                        )}
                      </div>
                    </td>

                    {/* 3. Duration */}
                    <td className="py-3.5 px-4 align-middle text-center">
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-ink-2 bg-surface-2 px-2 py-1 rounded-[6px] border border-line">
                        <Clock className="h-3.5 w-3.5 text-ink-3" />
                        {durationStr}
                      </span>
                    </td>

                    {/* 4. Posted — when it went up, not when the class was taught */}
                    <td className="py-3.5 px-4 align-middle">
                      <span className="text-xs font-bold text-ink">
                        {formatInstituteDateTime(recording.postedAt)}
                      </span>
                    </td>

                    {/* 5. Views */}
                    <td className="py-3.5 px-4 align-middle text-center">
                      <div className="inline-flex items-center gap-1.5 text-xs font-bold text-ink">
                        <Eye className="h-3.5 w-3.5 text-ink-3" />
                        <span>{recording.viewCount ?? 0}</span>
                      </div>
                    </td>

                    {/* 6. Actions */}
                    <td className="py-3.5 px-4 align-middle text-center">
                      <div className="inline-flex items-center justify-center gap-1.5">
                        {/* Play Button */}
                        <button
                          type="button"
                          title="Watch lesson"
                          onClick={() => openLesson(recording.uuid)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500/15 text-indigo-500 border border-indigo-500/30 hover:bg-indigo-600 hover:text-white transition-all cursor-pointer shadow-2xs"
                        >
                          <Play className="h-3.5 w-3.5 fill-current" />
                        </button>

                        {/* Download Button */}
                        <button
                          type="button"
                          title="Download or open link"
                          onClick={() => {
                            const url =
                              recording.downloadUrl || recording.playbackUrl || recording.videoUrl;
                            if (url) {
                              window.open(url, '_blank', 'noopener,noreferrer');
                            } else {
                              openLesson(recording.uuid);
                            }
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/15 text-blue-500 border border-blue-500/30 hover:bg-blue-600 hover:text-white transition-all cursor-pointer shadow-2xs"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>

                        {/* More Menu Dropdown (Portalled so it never clips) */}
                        <Menu
                          label={`Actions for ${recording.title || 'lesson'}`}
                          align="end"
                          trigger={<MoreHorizontal className="h-4 w-4" />}
                          triggerClassName="flex h-8 w-8 items-center justify-center rounded-lg bg-surface-2 text-ink-3 border border-line hover:text-ink transition-all cursor-pointer shadow-2xs"
                          items={actionsFor(recording)}
                        />
                      </div>
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
